import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, X, GripVertical, Play, User, Calendar, Loader2, ClipboardList, AlertTriangle, TrendingUp, Circle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { storage, createSession, getDefaultTemplate, OBSERVATION_CONTEXTS } from '../lib/storage';
import { fetchObservationTemplates, fetchDefaultObservationTemplate } from '../lib/observationTemplatesApi';
import { fetchReflectionTemplates } from '../lib/reflectionTemplatesApi';
import { fetchCoaches } from '../lib/offlineApi';
import { fetchBulkCoachObservationStatus, formatObservationLimit, getObservationLimitClass, canObserveCoach, fetchLimitsSummary } from '../lib/subscriptionApi';
import { generateId, cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useCloudSync } from '../contexts/CloudSyncContext';
import { safeGet } from '../lib/safeFetch';

const API_URL = '';

export default function SessionSetup() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedCoachId = searchParams.get('coachId');
  const plannedDate = searchParams.get('date');
  const isEditing = !!sessionId;
  const { isCoachDeveloper } = useAuth();
  const { getSession, saveSession: cloudSaveSession, setCurrentSession } = useCloudSync();
  
  const [templates, setTemplates] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCoachId, setSelectedCoachId] = useState(preselectedCoachId || 'none');
  const [observationContext, setObservationContext] = useState(OBSERVATION_CONTEXTS.TRAINING);
  const [sessionDate, setSessionDate] = useState(plannedDate || '');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  
  // Session parts state
  const [showCustomPartDialog, setShowCustomPartDialog] = useState(false);
  const [customPartName, setCustomPartName] = useState('');
  const [savingPart, setSavingPart] = useState(false);
  
  // Reflection template state (Phase 4)
  // Observer reflection template (for coach developer's reflection)
  const [reflectionTemplates, setReflectionTemplates] = useState([]);
  const [selectedReflectionTemplateId, setSelectedReflectionTemplateId] = useState('default');
  const [enableObserverNotes, setEnableObserverNotes] = useState(true);
  
  // Coach reflection template (for the coach's reflection on their session)
  const [coachReflectionTemplates, setCoachReflectionTemplates] = useState([]);
  const [selectedCoachReflectionTemplateId, setSelectedCoachReflectionTemplateId] = useState('default');

  // Phase 4: Observation limits state
  const [coachObservationStatus, setCoachObservationStatus] = useState({}); // Map of coach_id -> status
  const [loadingLimits, setLoadingLimits] = useState(false);
  
  // Subscription tier state - Individual Coach tier doesn't need observer reflection templates
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const isIndividualCoachTier = subscriptionTier === 'individual_coach';
  
  // For Individual Coach tier: auto-assign self as the coach being observed
  const [selfCoachProfile, setSelfCoachProfile] = useState(null);

  useEffect(() => {
    const initSession = async () => {
      setLoading(true);
      loadCoaches();
      loadSubscriptionTier();
      loadCoachReflectionTemplates();
      
      if (isEditing) {
        // Load existing session from cloud
        try {
          const existing = await getSession(sessionId);
          if (existing) {
            setSession(existing);
            setSelectedCoachId(existing.coachId || 'none');
            // Also restore the planned date if it exists
            if (existing.plannedDate) {
              setSessionDate(existing.plannedDate);
            }
            // Restore coach reflection template selection
            if (existing.coachReflectionTemplateId) {
              setSelectedCoachReflectionTemplateId(existing.coachReflectionTemplateId);
            }
            // Restore observer reflection template selection
            if (existing.reflectionTemplateId) {
              setSelectedReflectionTemplateId(existing.reflectionTemplateId);
            }
          } else {
            toast.error('Session not found');
            navigate('/');
          }
        } catch (err) {
          console.error('Failed to load session:', err);
          toast.error('Failed to load session');
          navigate('/');
        }
        // Load templates for editing (but don't apply)
        await loadObservationTemplates(false);
      } else {
        // For NEW sessions: Create a basic session shell first
        const baseSession = {
          id: generateId('session'),
          name: '',
          coachId: preselectedCoachId || null,
          observationContext: OBSERVATION_CONTEXTS.TRAINING,
          plannedDate: plannedDate || null,
          planned: !!plannedDate,
          status: plannedDate ? 'planned' : 'draft',
          createdAt: new Date().toISOString(),
          events: [],
          notes: [],
          sessionParts: [], // Start empty - will be populated by template
          interventionTypes: [],
          eventTypes: [],
          descriptorGroup1: { id: 'group1', name: 'Group 1', color: 'blue', descriptors: [] },
          descriptorGroup2: { id: 'group2', name: 'Group 2', color: 'green', descriptors: [] }
        };
        setSession(baseSession);
        if (plannedDate) setSessionDate(plannedDate);
        
        // NOW load templates and apply default template to session
        await loadObservationTemplates(true);
      }
      setLoading(false);
    };
    
    initSession();
  }, [sessionId, isEditing, navigate, preselectedCoachId, plannedDate, getSession]);

  // Load observation templates from API
  const loadObservationTemplates = async (applyDefault = false) => {
    setLoadingTemplates(true);
    try {
      const apiTemplates = await fetchObservationTemplates();
      if (apiTemplates && apiTemplates.length > 0) {
        setTemplates(apiTemplates);
        
        if (applyDefault) {
          // Select the default template for the current context
          const defaultTemplate = apiTemplates.find(t => t.isDefault && t.observationContext === 'training');
          if (defaultTemplate) {
            setSelectedTemplate(defaultTemplate.id);
            // Apply template's session parts to the new session
            applyTemplateToSession(defaultTemplate);
          } else if (apiTemplates.length > 0) {
            // No default found, use the first template
            setSelectedTemplate(apiTemplates[0].id);
            applyTemplateToSession(apiTemplates[0]);
          }
        } else if (apiTemplates.length > 0) {
          // Just set the selected template without applying (for editing existing sessions)
          setSelectedTemplate(apiTemplates[0].id);
        }
      } else {
        // No templates from API - fall back to localStorage (matches TemplateManager behavior)
        console.warn('[SessionSetup] No templates from API, using localStorage fallback');
        const localTemplates = storage.getTemplates();
        if (localTemplates && localTemplates.length > 0) {
          console.log('[SessionSetup] Using localStorage templates:', localTemplates.length);
          setTemplates(localTemplates);
          setSelectedTemplate(localTemplates[0].id || 'default');
          if (applyDefault) {
            applyTemplateToSession(localTemplates[0]);
          }
        } else {
          // No templates anywhere - show empty state
          console.warn('[SessionSetup] No templates available anywhere');
          setTemplates([]);
        }
      }
    } catch (err) {
      console.error('Failed to load observation templates:', err);
      // Fallback to localStorage on error (matches TemplateManager behavior)
      console.log('[SessionSetup] Error - using localStorage templates as fallback');
      const localTemplates = storage.getTemplates();
      if (localTemplates && localTemplates.length > 0) {
        setTemplates(localTemplates);
        setSelectedTemplate(localTemplates[0].id || 'default');
        if (applyDefault) {
          applyTemplateToSession(localTemplates[0]);
        }
      } else {
        toast.error('Failed to load templates. Please check your connection.');
        setTemplates([]);
      }
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Apply template configuration to session
  const applyTemplateToSession = (template) => {
    if (!template) return;
    
    const interventions = template.interventionTypes || template.eventTypes || [];
    setSession(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        interventionTypes: [...interventions],
        eventTypes: [...interventions],
        includeBallRolling: template.includeBallRolling !== false, // Default true for backwards compat
        descriptorGroup1: template.descriptorGroup1 ? { 
          ...template.descriptorGroup1, 
          descriptors: [...(template.descriptorGroup1.descriptors || [])] 
        } : prev.descriptorGroup1,
        descriptorGroup2: template.descriptorGroup2 ? { 
          ...template.descriptorGroup2, 
          descriptors: [...(template.descriptorGroup2.descriptors || [])] 
        } : prev.descriptorGroup2,
        // Use ONLY the template's session parts - no mixing with defaults
        sessionParts: (template.sessionParts || []).map(p => ({
          id: p.id || generateId('part'),
          name: p.name,
          order: p.order,
          startTime: null,
          endTime: null,
          ballRollingTime: 0,
          ballNotRollingTime: 0,
          used: false
        })),
        // Store which template was used for this session
        templateId: template.id || template.templateId,
        templateName: template.name
      };
    });
  };

  // Load coaches from API with offline fallback
  const loadCoaches = async () => {
    try {
      const result = await fetchCoaches();
      if (result.ok && result.data) {
        setCoaches(result.data);
        
        // Phase 4: Fetch observation limits for all coaches
        const coachIds = result.data.map(c => c.id);
        if (coachIds.length > 0) {
          setLoadingLimits(true);
          try {
            const limitsResult = await fetchBulkCoachObservationStatus(coachIds);
            if (limitsResult.ok && limitsResult.data) {
              setCoachObservationStatus(limitsResult.data);
            }
          } catch (limitErr) {
            console.error('[SessionSetup] Failed to load observation limits:', limitErr);
          } finally {
            setLoadingLimits(false);
          }
        }
        
        // If coach is preselected, set a default session name with proper format
        if (preselectedCoachId) {
          const coach = result.data.find(c => c.id === preselectedCoachId);
          if (coach) {
            const today = new Date();
            const day = String(today.getDate()).padStart(2, '0');
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const year = String(today.getFullYear()).slice(-2);
            const autoName = `${coach.name} - ${day}/${month}/${year}`;
            
            setSession(prev => prev ? {
              ...prev,
              name: autoName
            } : prev);
          }
        }
        
        if (result.fromCache) {
          console.log('[SessionSetup] Loaded coaches from offline cache');
        }
      }
    } catch (err) {
      console.error('Failed to load coaches:', err);
      // Fallback to localStorage if API fails
      setCoaches(storage.getCoaches());
    }
  };

  const loadReflectionTemplates = async () => {
    try {
      // Load coach educator reflection templates (only for non-Individual Coach tiers)
      const templates = await fetchReflectionTemplates('coach_educator');
      setReflectionTemplates(templates);
      
      // Set default template if one exists
      const defaultTemplate = templates.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedReflectionTemplateId(defaultTemplate.template_id);
      }
    } catch (err) {
      console.error('Failed to load reflection templates:', err);
    }
  };
  
  // Load subscription tier to determine which reflection templates to show
  // For Individual Coach tier, also fetch/create self coach profile
  const loadSubscriptionTier = async () => {
    try {
      const result = await fetchLimitsSummary();
      if (result.ok && result.data) {
        setSubscriptionTier(result.data.tier_key);
        
        // For Individual Coach tier: fetch or create self as coach
        if (result.data.tier_key === 'individual_coach') {
          await loadSelfCoachProfile();
        } else {
          // Only load observer reflection templates for non-Individual Coach tiers
          loadReflectionTemplates();
        }
      }
    } catch (err) {
      console.error('Failed to load subscription tier:', err);
      // Fallback: load templates anyway
      loadReflectionTemplates();
    }
  };
  
  // For Individual Coach tier: Get or create a coach profile for self-observation
  const loadSelfCoachProfile = async () => {
    try {
      // First, check if user already has a linked coach profile
      const userResult = await safeGet(`${API_URL}/api/auth/me`);
      if (userResult.ok && userResult.data) {
        const userData = userResult.data;
        
        // Helper to generate session name inline
        const genName = (name) => {
          if (!name) return '';
          const today = new Date();
          const day = String(today.getDate()).padStart(2, '0');
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const year = String(today.getFullYear()).slice(-2);
          return `${name} - ${day}/${month}/${year}`;
        };
        
        // If user has a linked coach, use that
        if (userData.linked_coach_id) {
          const coachResult = await safeGet(`${API_URL}/api/coaches/${userData.linked_coach_id}`);
          if (coachResult.ok && coachResult.data) {
            const coachData = coachResult.data;
            setSelfCoachProfile(coachData);
            // Auto-select self as coach and auto-generate session name
            setSelectedCoachId(userData.linked_coach_id);
            updateSession({ 
              coachId: userData.linked_coach_id,
              name: genName(coachData.name)
            });
            return;
          }
        }
        
        // Otherwise, create a self-coach profile using user's name
        // For Individual Coach tier, we create a coach profile that represents themselves
        const selfCoach = {
          id: `self_${userData.user_id}`,
          name: userData.name || 'Self',
          role_title: 'Individual Coach',
          isSelf: true
        };
        setSelfCoachProfile(selfCoach);
        setSelectedCoachId(selfCoach.id);
        updateSession({ 
          coachId: selfCoach.id,
          coachName: selfCoach.name,
          name: genName(selfCoach.name) // Auto-generate session name
        });
      }
    } catch (err) {
      console.error('Failed to load self coach profile:', err);
    }
  };

  // Load coach reflection templates (for the coach's reflection on their session)
  const loadCoachReflectionTemplates = async () => {
    try {
      const templates = await fetchReflectionTemplates('coach');
      setCoachReflectionTemplates(templates);
      
      // Set default coach template if one exists
      const defaultTemplate = templates.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedCoachReflectionTemplateId(defaultTemplate.template_id);
        // Auto-save to session
        updateSession({ coachReflectionTemplateId: defaultTemplate.template_id });
      }
    } catch (err) {
      console.error('Failed to load coach reflection templates:', err);
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      applyTemplateToSession(template);
    }
  };

  const handleCoachChange = (coachId) => {
    setSelectedCoachId(coachId);
    const coach = coaches.find(c => c.id === coachId);
    const coachName = coach?.name || null;
    
    // Auto-generate session name: "Coach Name - DD/MM/YY"
    const autoName = generateSessionName(coachName);
    
    updateSession({ 
      coachId: coachId === 'none' ? null : coachId,
      name: autoName
    });
  };
  
  // Generate session name in format "Coach Name - DD/MM/YY"
  const generateSessionName = (coachName) => {
    if (!coachName) return '';
    
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear()).slice(-2);
    
    return `${coachName} - ${day}/${month}/${year}`;
  };

  const handleContextChange = (context) => {
    setObservationContext(context);
    updateSession({ observationContext: context });
  };

  const handleDateChange = (date) => {
    setSessionDate(date);
    updateSession({ 
      plannedDate: date || null,
      status: date ? 'planned' : 'draft'
    });
  };

  const handleReflectionTemplateChange = (templateId) => {
    setSelectedReflectionTemplateId(templateId);
    updateSession({ reflectionTemplateId: templateId === 'default' ? null : templateId });
  };

  // Handle coach reflection template selection
  const handleCoachReflectionTemplateChange = (templateId) => {
    setSelectedCoachReflectionTemplateId(templateId);
    updateSession({ coachReflectionTemplateId: templateId === 'default' ? null : templateId });
  };

  const handleNotesToggle = (enabled) => {
    setEnableObserverNotes(enabled);
    updateSession({ enableObserverNotes: enabled });
  };

  const updateSession = (updates) => {
    setSession(prev => ({ ...prev, ...updates }));
  };

  // Event type management
  const addEventType = () => {
    const currentEventTypes = session.eventTypes || session.interventionTypes || [];
    const newEvent = { id: generateId('event'), name: 'New Event', color: 'yellow' };
    updateSession({ eventTypes: [...currentEventTypes, newEvent], interventionTypes: [...currentEventTypes, newEvent] });
  };

  const updateEventType = (id, name) => {
    const currentEventTypes = session.eventTypes || session.interventionTypes || [];
    const updated = currentEventTypes.map(e => e.id === id ? { ...e, name } : e);
    updateSession({
      eventTypes: updated,
      interventionTypes: updated
    });
  };

  const removeEventType = (id) => {
    const currentEventTypes = session.eventTypes || session.interventionTypes || [];
    if (currentEventTypes.length <= 1) {
      toast.error('You need at least one event type');
      return;
    }
    const filtered = currentEventTypes.filter(e => e.id !== id);
    updateSession({ eventTypes: filtered, interventionTypes: filtered });
  };

  // Descriptor management
  const addDescriptor = (groupNum) => {
    const groupKey = `descriptorGroup${groupNum}`;
    const group = session[groupKey] || { name: `Descriptor Group ${groupNum}`, descriptors: [] };
    const newDesc = { id: generateId('desc'), name: 'New' };
    updateSession({
      [groupKey]: { ...group, descriptors: [...(group.descriptors || []), newDesc] }
    });
  };

  const updateDescriptor = (groupNum, id, name) => {
    const groupKey = `descriptorGroup${groupNum}`;
    const group = session[groupKey] || { name: `Descriptor Group ${groupNum}`, descriptors: [] };
    updateSession({
      [groupKey]: {
        ...group,
        descriptors: (group.descriptors || []).map(d => d.id === id ? { ...d, name } : d)
      }
    });
  };

  const removeDescriptor = (groupNum, id) => {
    const groupKey = `descriptorGroup${groupNum}`;
    const group = session[groupKey] || { name: `Descriptor Group ${groupNum}`, descriptors: [] };
    updateSession({
      [groupKey]: { ...group, descriptors: (group.descriptors || []).filter(d => d.id !== id) }
    });
  };

  const updateGroupName = (groupNum, name) => {
    const groupKey = `descriptorGroup${groupNum}`;
    const group = session[groupKey] || { name: `Descriptor Group ${groupNum}`, descriptors: [] };
    updateSession({
      [groupKey]: { ...group, name }
    });
  };

  // Session parts management - now uses global defaults from backend
  const addSessionPartFromDefault = (part) => {
    const newPart = {
      id: generateId('part'),
      name: part.name,
      partId: part.part_id, // Reference to the global part
      order: (session.sessionParts || []).length,
      isDefault: part.is_default,
      startTime: null,
      endTime: null,
      ballRollingTime: 0,
      ballNotRollingTime: 0
    };
    updateSession({ sessionParts: [...(session.sessionParts || []), newPart] });
  };

  const handleAddCustomPart = () => {
    setCustomPartName('');
    setShowCustomPartDialog(true);
  };

  const handleSaveCustomPart = async (addAsDefault) => {
    if (!customPartName.trim()) {
      toast.error('Please enter a part name');
      return;
    }

    setSavingPart(true);
    try {
      if (addAsDefault && isCoachDeveloper()) {
        // Create as new global default
        const newPart = await createSessionPart(customPartName.trim(), true);
        await loadSessionParts(); // Refresh the list
        addSessionPartFromDefault(newPart);
        toast.success('Session part added as new default');
      } else {
        // Add as one-off custom part for this session only
        const newPart = {
          id: generateId('part'),
          name: customPartName.trim(),
          order: (session.sessionParts || []).length,
          isDefault: false,
          isCustom: true,
          startTime: null,
          endTime: null,
          ballRollingTime: 0,
          ballNotRollingTime: 0
        };
        updateSession({ sessionParts: [...(session.sessionParts || []), newPart] });
        toast.success('Custom session part added');
      }
      setShowCustomPartDialog(false);
      setCustomPartName('');
    } catch (err) {
      toast.error(err.message || 'Failed to create session part');
    } finally {
      setSavingPart(false);
    }
  };

  const addSessionPart = () => {
    // Legacy function - now opens dialog for custom part
    handleAddCustomPart();
  };

  const updateSessionPart = (id, name) => {
    updateSession({
      sessionParts: (session.sessionParts || []).map(p => p.id === id ? { ...p, name } : p)
    });
  };

  const removeSessionPart = (id) => {
    if ((session.sessionParts || []).length <= 1) {
      toast.error('You need at least one session part');
      return;
    }
    updateSession({
      sessionParts: (session.sessionParts || []).filter(p => p.id !== id),
      activePartId: session.activePartId === id ? session.sessionParts[0].id : session.activePartId
    });
  };

  const handleSave = async () => {
    if (!session.name.trim()) {
      toast.error('Please enter a session name');
      return;
    }
    
    const updated = {
      ...session,
      updatedAt: new Date().toISOString(),
      activePartId: session.activePartId || session.sessionParts[0]?.id
    };
    
    // Save to cloud and cache
    setCurrentSession(updated);
    await cloudSaveSession(updated);
    toast.success('Session saved');
    navigate('/');
  };

  const handleStartObservation = async () => {
    if (!session.name.trim()) {
      toast.error('Please enter a session name');
      return;
    }
    
    // Validate coach selection
    if (!selectedCoachId || selectedCoachId === 'none') {
      toast.error('Please select a coach for this observation');
      return;
    }
    
    // Phase 4: Check observation limit before starting
    const status = coachObservationStatus[selectedCoachId];
    if (status) {
      const canObserve = canObserveCoach(status);
      if (!canObserve.allowed) {
        toast.error(canObserve.reason || 'Observation limit reached for this coach');
        return;
      }
    }
    
    const updated = {
      ...session,
      updatedAt: new Date().toISOString(),
      activePartId: session.activePartId || session.sessionParts[0]?.id
    };
    
    // Save to cloud and cache before navigating
    setCurrentSession(updated);
    await cloudSaveSession(updated);
    navigate(`/session/${session.id}/observe`);
  };

  if (!session || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Ensure session has all required properties with defaults
  const eventTypes = session.eventTypes || session.interventionTypes || [];
  const descriptorGroup1 = session.descriptorGroup1 || { name: 'Descriptor Group 1', descriptors: [] };
  const descriptorGroup2 = session.descriptorGroup2 || { name: 'Descriptor Group 2', descriptors: [] };
  const sessionParts = session.sessionParts || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn" className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope'] truncate">
                {isEditing ? 'Edit Session' : 'New Session'}
              </h1>
            </div>
          </div>
          <div className="flex gap-1.5 sm:gap-2 shrink-0">
            <Button variant="outline" onClick={handleSave} data-testid="save-session-btn" size="sm" className="px-2 sm:px-3">
              Save
            </Button>
            <Button onClick={handleStartObservation} className="bg-orange-500 hover:bg-orange-600 px-2 sm:px-3" data-testid="start-observation-btn" size="sm">
              <Play className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Start Observation</span>
              <span className="sm:hidden">Start</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Coach Selection - Hidden for Individual Coach tier (auto-assigned to self) */}
        {!isIndividualCoachTier && (
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <User className="w-5 h-5" />
                Coach
              </CardTitle>
              <CardDescription>
                Select the coach being observed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={selectedCoachId} onValueChange={handleCoachChange}>
                <SelectTrigger data-testid="coach-select" className={selectedCoachId === 'none' ? 'text-slate-400' : ''}>
                  <SelectValue placeholder="Select a coach..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-slate-500">No linked coach</SelectItem>
                  {coaches.map(c => {
                    const status = coachObservationStatus[c.id];
                    const canObserve = canObserveCoach(status);
                    const limitText = status ? formatObservationLimit(status) : '';
                    const limitClass = status ? getObservationLimitClass(status) : '';
                    const isCoachDev = c.is_coach_developer;
                    
                    return (
                      <SelectItem 
                        key={c.id} 
                        value={c.id}
                        disabled={!canObserve.allowed}
                        className={!canObserve.allowed ? 'opacity-60' : ''}
                      >
                        <div className="flex items-center justify-between w-full gap-3">
                          <span className={!canObserve.allowed ? 'text-slate-400' : ''}>
                            {c.name}
                            {isCoachDev && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                Coach Developer
                              </span>
                            )}
                            {!isCoachDev && c.role_title && ` - ${c.role_title}`}
                          </span>
                          {status && (
                            <span className={cn("text-xs font-medium ml-auto", limitClass)}>
                              {limitText}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {/* Phase 4: Show observation limit status for selected coach */}
              {selectedCoachId && selectedCoachId !== 'none' && coachObservationStatus[selectedCoachId] && (
                <div className="mt-2">
                  {(() => {
                    const status = coachObservationStatus[selectedCoachId];
                    const canObserve = canObserveCoach(status);
                    const selectedCoach = coaches.find(c => c.id === selectedCoachId);
                    
                    if (!canObserve.allowed) {
                      return (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-red-800">Observation limit reached</p>
                            <p className="text-sm text-red-600 mt-0.5">
                              {selectedCoach?.name} has {status.current_count} of {status.limit} observations.
                              Upgrade your subscription to continue observing this coach.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    
                    if (canObserve.showWarning) {
                      return (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-amber-800">Approaching limit</p>
                            <p className="text-sm text-amber-600 mt-0.5">{canObserve.reason}</p>
                          </div>
                        </div>
                      );
                    }
                    
                    // Show normal status
                    if (!status.is_unlimited && status.limit) {
                      return (
                        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          <TrendingUp className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-600">
                            {selectedCoach?.name}: <span className={cn("font-medium", getObservationLimitClass(status))}>
                              {status.current_count} of {status.limit}
                            </span> observations used
                          </span>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                </div>
              )}
              
              {loadingLimits && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading observation limits...
                </div>
              )}
              
              {coaches.length === 0 && (
                <p className="text-sm text-slate-500 mt-2">
                  No coaches yet. <button className="text-blue-600 hover:underline" onClick={() => navigate('/coaches')}>Add a coach</button> to track their development over time.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Observation Context */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope']">Observation Type</CardTitle>
            <CardDescription>
              Select the context for this observation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleContextChange(OBSERVATION_CONTEXTS.TRAINING)}
                className={cn(
                  "p-4 rounded-lg border-2 text-left transition-all",
                  observationContext === OBSERVATION_CONTEXTS.TRAINING
                    ? "border-blue-500 bg-blue-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
                data-testid="context-training"
              >
                <div className="font-semibold text-slate-900">Training Observation</div>
                <div className="text-sm text-slate-500 mt-1">Observe a training session</div>
              </button>
              <button
                type="button"
                onClick={() => handleContextChange(OBSERVATION_CONTEXTS.GAME)}
                className={cn(
                  "p-4 rounded-lg border-2 text-left transition-all",
                  observationContext === OBSERVATION_CONTEXTS.GAME
                    ? "border-green-500 bg-green-50"
                    : "border-slate-200 hover:border-slate-300"
                )}
                data-testid="context-game"
              >
                <div className="font-semibold text-slate-900">Game Observation</div>
                <div className="text-sm text-slate-500 mt-1">Observe during a match or game</div>
              </button>
            </div>
            
            {/* Planned Date */}
            <div>
              <Label htmlFor="session-date" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Schedule for Later (Optional)
              </Label>
              <Input
                id="session-date"
                type="date"
                value={sessionDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="mt-1 max-w-xs"
                data-testid="session-date-input"
              />
              {sessionDate && (
                <p className="text-sm text-slate-500 mt-1">
                  This session will be marked as planned and appear in your calendar.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Session Name & Template */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope']">Session Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="session-name">Session Name</Label>
              <Input
                id="session-name"
                value={session.name}
                onChange={(e) => updateSession({ name: e.target.value })}
                placeholder="e.g., U14 Training - 15 Jan"
                className="mt-1"
                data-testid="session-name-input"
              />
            </div>
            {!isEditing && (
              <div>
                <Label htmlFor="template">Start from Template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger className="mt-1" data-testid="template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {(t.isDefault || t.is_default) && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reflection & Notes Settings (Phase 4) */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Reflection & Notes
            </CardTitle>
            <CardDescription>
              Configure post-observation reflection and notes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Coach Reflection Template Selector - This is what the coach will see */}
            <div>
              <Label htmlFor="coach-reflection-template">Coach Reflection Template</Label>
              <p className="text-sm text-slate-500 mb-2">
                Select the template the coach will use for their reflection on this session
              </p>
              <Select 
                value={selectedCoachReflectionTemplateId} 
                onValueChange={handleCoachReflectionTemplateChange}
              >
                <SelectTrigger className="mt-1" data-testid="coach-reflection-template-select">
                  <SelectValue placeholder="Select a reflection template for the coach" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    Use Default Template
                  </SelectItem>
                  {coachReflectionTemplates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.name} {t.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {coachReflectionTemplates.length === 0 && (
                <p className="text-sm text-slate-500 mt-2">
                  No coach reflection templates yet.{' '}
                  <button 
                    className="text-blue-600 hover:underline" 
                    onClick={() => navigate('/templates')}
                  >
                    Create one
                  </button>
                </p>
              )}
            </div>

            {/* Observer Reflection Template Selector - Only for Coach Developer/Club tiers */}
            {!isIndividualCoachTier && (
              <div className="pt-4 border-t border-slate-200">
                <Label htmlFor="reflection-template">Observer Reflection Template</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Select the template for your own post-observation reflection
                </p>
                <Select 
                  value={selectedReflectionTemplateId} 
                  onValueChange={handleReflectionTemplateChange}
                >
                  <SelectTrigger className="mt-1" data-testid="reflection-template-select">
                    <SelectValue placeholder="Select a reflection template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">
                      Use Default Template
                    </SelectItem>
                    {reflectionTemplates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name} {t.is_default && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {reflectionTemplates.length === 0 && (
                  <p className="text-sm text-slate-500 mt-2">
                    No observer reflection templates yet.{' '}
                    <button 
                      className="text-blue-600 hover:underline" 
                      onClick={() => navigate('/templates')}
                    >
                      Create one
                    </button>
                  </p>
                )}
              </div>
            )}

            {/* Observer Notes Toggle */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div>
                <Label htmlFor="enable-notes" className="cursor-pointer">Enable Observer Notes</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Take private notes during observation (can be added to summary later)
                </p>
              </div>
              <Switch
                id="enable-notes"
                checked={enableObserverNotes}
                onCheckedChange={handleNotesToggle}
                data-testid="enable-notes-switch"
              />
            </div>

            {/* Ball Rolling Toggle */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <Circle className="w-4 h-4 text-orange-600 fill-orange-600" />
                </div>
                <div>
                  <Label htmlFor="include-ball-rolling" className="cursor-pointer">Track Ball Rolling</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Record ball rolling / stopped time during observation
                  </p>
                </div>
              </div>
              <Switch
                id="include-ball-rolling"
                checked={session.includeBallRolling !== false}
                onCheckedChange={(checked) => updateSession({ includeBallRolling: checked })}
                data-testid="include-ball-rolling-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Event Types */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Manrope']">Event Types</CardTitle>
            <Button size="sm" variant="outline" onClick={addEventType} data-testid="add-event-type-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">These are the coaching interventions you want to track (shown as yellow buttons).</p>
            <div className="space-y-2">
              {eventTypes.map((event) => (
                <div key={event.id} className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-slate-400" />
                  <div className="w-4 h-4 rounded bg-yellow-400" />
                  <Input
                    value={event.name}
                    onChange={(e) => updateEventType(event.id, e.target.value)}
                    className="flex-1"
                    data-testid={`event-type-input-${event.id}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => removeEventType(event.id)}
                    data-testid={`remove-event-${event.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Descriptor Group 1 */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-sky-400" />
              <Input
                value={descriptorGroup1.name}
                onChange={(e) => updateGroupName(1, e.target.value)}
                className="font-semibold border-0 p-0 h-auto text-lg focus-visible:ring-0"
                data-testid="descriptor-group-1-name"
              />
            </div>
            <Button size="sm" variant="outline" onClick={() => addDescriptor(1)} data-testid="add-descriptor-1-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(descriptorGroup1.descriptors || []).map((desc) => (
                <div key={desc.id} className="flex items-center gap-1 bg-sky-100 rounded-lg pl-3 pr-1 py-1">
                  <Input
                    value={desc.name}
                    onChange={(e) => updateDescriptor(1, desc.id, e.target.value)}
                    className="border-0 bg-transparent p-0 h-auto w-24 focus-visible:ring-0"
                    data-testid={`descriptor-1-input-${desc.id}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-slate-400 hover:text-red-600"
                    onClick={() => removeDescriptor(1, desc.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Descriptor Group 2 */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-400" />
              <Input
                value={descriptorGroup2.name}
                onChange={(e) => updateGroupName(2, e.target.value)}
                className="font-semibold border-0 p-0 h-auto text-lg focus-visible:ring-0"
                data-testid="descriptor-group-2-name"
              />
            </div>
            <Button size="sm" variant="outline" onClick={() => addDescriptor(2)} data-testid="add-descriptor-2-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(descriptorGroup2.descriptors || []).map((desc) => (
                <div key={desc.id} className="flex items-center gap-1 bg-green-100 rounded-lg pl-3 pr-1 py-1">
                  <Input
                    value={desc.name}
                    onChange={(e) => updateDescriptor(2, desc.id, e.target.value)}
                    className="border-0 bg-transparent p-0 h-auto w-24 focus-visible:ring-0"
                    data-testid={`descriptor-2-input-${desc.id}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-slate-400 hover:text-red-600"
                    onClick={() => removeDescriptor(2, desc.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Session Parts - From Template */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-['Manrope']">Session Parts</CardTitle>
              <CardDescription>Parts from selected template. You can edit names or add custom parts.</CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddCustomPart}
              className="border-dashed"
              data-testid="add-custom-part-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Part
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected Session Parts */}
            <div>
              {(session.sessionParts || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4">No parts yet. Add a part to get started.</p>
              ) : (
                <div className="space-y-2">
                  {(session.sessionParts || []).map((part, index) => (
                    <div key={part.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-400 w-6 font-medium">{index + 1}</span>
                      <Input
                        value={part.name}
                        onChange={(e) => updateSessionPart(part.id, e.target.value)}
                        className="flex-1 bg-white"
                        placeholder="Part name"
                        data-testid={`session-part-input-${part.id}`}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-slate-400 hover:text-red-600 h-8 w-8"
                        onClick={() => removeSessionPart(part.id)}
                        title="Remove part"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Custom Part Dialog */}
        <Dialog open={showCustomPartDialog} onOpenChange={setShowCustomPartDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Custom Session Part</DialogTitle>
              <DialogDescription>
                Enter a name for your custom session part.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="custom-part-name">Part Name</Label>
              <Input
                id="custom-part-name"
                value={customPartName}
                onChange={(e) => setCustomPartName(e.target.value)}
                placeholder="e.g., Cool Down"
                className="mt-1"
                data-testid="custom-part-name-input"
              />
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => handleSaveCustomPart(false)}
                disabled={savingPart || !customPartName.trim()}
              >
                {savingPart ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add as One-Off
              </Button>
              {isCoachDeveloper() && (
                <Button
                  onClick={() => handleSaveCustomPart(true)}
                  disabled={savingPart || !customPartName.trim()}
                >
                  {savingPart ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add as New Default
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
